import * as React from 'react';
import { inject, observer } from 'mobx-react';
import { IAGroupList } from '../../../groupList/GroupList';
import { GroupListGroup, GroupList, GroupListItem, GroupListColumnType } from '../../../../interfaces/IGroupList'
import { IApplication, Instance, IStoredObject } from '../../../../interfaces/IApplication';
import { IAIcon } from '../../../icon/Icon';
import { ApplicationsSettings } from './ApplicationsSettings';
import { TargetingAdminStore } from '../../stores/TargetingAdminStore';
import { IASpinner } from '../../../spinner/Spinner';
import { LocalizationService } from './../../../../services/LocalizationService';
import { ITenantSettings } from '../../../../interfaces/ITenantSettings';
import { TenantSettingsService } from 'intraactive-sdk-helper';
import { Environment } from '../../../../services/Environment';
import DateTimeService from '../../../../services/DateTimeService';
import { sdkContainerInstance } from '../../../global';
import { Helper } from '../../../../Helper';
import { IAMessageBar } from '../../../messageBar/MessageBar';

export interface IApplicationsProps {
  targetingStore?: TargetingAdminStore;
}

export interface IApplicationsState {
  openPanel: boolean;
  itemSelected: Instance;
  applicationSelected: IApplication;
}

@inject('targetingStore')
@observer
export class Applications extends React.Component<IApplicationsProps, IApplicationsState> {

  private readonly tenantSettingsService: TenantSettingsService<ITenantSettings>;
  private readonly localizationService: LocalizationService = new LocalizationService();

  constructor(props: IApplicationsProps, state: IApplicationsState) {
    super(props, state);
    this.state = {
      openPanel: false,
      applicationSelected: undefined,
      itemSelected: undefined,
    }
    this.localizationService?.checkLocalizedStrings().then(() => this.forceUpdate());
    if (this.props.targetingStore.language != undefined) {
      this.props.targetingStore.setLanguage(this.props.targetingStore.language);
    }
    this.tenantSettingsService = new TenantSettingsService<ITenantSettings>(Environment.getEnvironmentForSettingsAPI(props.targetingStore.environment), sdkContainerInstance);
    if (this.tenantSettingsService && props.targetingStore.tenant && props.targetingStore.token) {
      const tenantSettings = localStorage.getItem("IA_tenantSettings");
      if (tenantSettings && new DateTimeService().getDatePlusTwoMinuts(new Date(JSON.parse(tenantSettings).date)) > new Date()) {
        console.log("Using cached tenant settings");
        props.targetingStore.settings = JSON.parse(tenantSettings).value;
      } else {
        console.log("Fetching tenant settings again");
        this.tenantSettingsService.getSettings(props.targetingStore.tenant, {}, props.targetingStore.token).then(setting => {
          props.targetingStore.settings = setting;
          Helper.setLocalStorage("IA_tenantSettings", JSON.stringify({
            date: new Date(),
            value: setting
          }));
        });
      }
    }
  }

  public render(): JSX.Element {

    if (!this.props.targetingStore.applicationsData && this.props.targetingStore.isLoadingApplication) {
      return (<div>
        <IASpinner />
      </div>);
    }

    let applicationsData = this.getApplicationsData(this.props.targetingStore.applicationsData);

    return (
      <div>
        <IAMessageBar
          type="information"
          content={this.localizationService.strings.TargetingAdmin_ApplicationDescription}
          style={{
            borderRadius: 5
          }}
          onDismiss={() => { }}
        />
        <div style={{
          float: 'left',
          width: "100%",
          marginTop: 40,
          paddingBottom: 50,
          boxSizing: "border-box"
        }}>
          <ApplicationsSettings
            close={() => this.setState({ openPanel: false })}
            open={this.state.openPanel}
            isApplication={this.state.itemSelected ? false : true}
            application={this.state.applicationSelected}
            instance={this.state.itemSelected}
            highlightColor={this.props.targetingStore.highlightColor}
          />
          {!this.props.targetingStore.isLoadingApplication &&
            <IAGroupList
              highlightColor={this.props.targetingStore.highlightColor}
              groupsItem={applicationsData}
              getUpdates={(items, selectedItem, selectedGroup) => {
                console.log(items);
                let data: Instance = undefined;
                let application: IApplication = undefined;
                if (selectedGroup) {
                  application = this.props.targetingStore.applicationsData.find(x => x.name === selectedGroup);
                  data = application.instances.find(x => x.id === selectedItem.id);
                } else {
                  application = this.props.targetingStore.applicationsData.find(x => x.name === selectedItem.title);
                }
                this.setState({ applicationSelected: application, openPanel: true, itemSelected: data });
              }}
            />}
        </div>
      </div>);
  }

  private getData(application: IApplication) {
    const instances = application.instances.sort((a1, a2) => {
      // defensive check to remove any null/undefined names before calling toLocaleLowerCase()
      const a1Name = a1.name ?? "";
      const a2Name = a2.name ?? "";

      if (a1Name.toLocaleLowerCase() > a2Name.toLocaleLowerCase()) {
        return 1;
      }
      if (a1Name.toLocaleLowerCase() < a2Name.toLocaleLowerCase()) {
        return -1;
      }
      return 0;
    });
    const items: GroupListItem[] = instances.map((item) => {
      const description = this.getSelectedValues(application, (item.type === application.default.type || application.name.toLocaleLowerCase() === 'content organizer') ? item.values : [], item.allValues, item.useDefault);
      return {
        title: item.name,
        id: item.id,
        name: item.name,
        columns: [
          {
            field: "column1",
            title: this.localizationService.strings.TargetingAdmin_ApplicationColumnName,
            type: GroupListColumnType.Text,
            value: item.name
          },
          {
            field: "column2",
            title: this.localizationService.strings.TargetingAdmin_ApplicationColumnProfileDataUserGroups,
            type: GroupListColumnType.Text,
            value: (<div>
              {item.isEnable &&
                <div style={{ position: 'relative', top: 3 }}>
                  <div style={{ float: 'left', position: 'relative', top: 5 }}>
                    <IAIcon
                      title={(application.name.toLocaleLowerCase() === 'content organizer' && item.type === "userGroups") ? "People" : (application.default.type === "userGroups") ? "People" : "Tag"}
                      color={"#333333"}
                      size={17} />
                  </div>
                  {(item.values && item.values.length > 0) &&
                    <div style={{
                      float: 'left',
                      position: 'relative',
                      top: 5,
                      marginLeft: 7,
                      width: "calc(100% - 40px)",
                      color: description === this.localizationService.strings.TargetingAdmin_HasDeletedGroups ? "red" : "#333333"
                    }}>
                      {description}
                    </div>
                  }
                </div>
              }
            </div>
            )
          },
          {
            field: "column3",
            title: "",
            type: GroupListColumnType.Icon,
            value: false
          },
        ]
      }

    });

    let displayColumn3 = true;
    if (application.name.toLocaleLowerCase() === 'content organizer') {
      displayColumn3 = false;
    }
    const description = this.getSelectedValues(application, application.default.values, application.default.allValues);
    const groupValues = {
      title: application.name,
      id: application.id,
      name: application.name,
      columns: [
        {
          field: "column2",
          title: this.localizationService.strings.TargetingAdmin_ApplicationColumnProfileDataUserGroups,
          type: GroupListColumnType.Text,
          value: (
            <div>{
              application.default.isEnable &&
              <div style={{ position: 'relative', top: '3px' }}>
                <div style={{ float: 'left', position: 'relative', top: '3px' }}>
                  <IAIcon
                    title={application.default.type === "userGroups" ? "People" : "Tag"}
                    color={"#333333"}
                    size={17} />
                </div>
                {(application.default.values && application.default.values.length > 0) &&
                  <div
                    style={{
                      float: 'left',
                      position: 'relative',
                      marginLeft: 7,
                      marginTop: 3,
                      color: description === this.localizationService.strings.TargetingAdmin_HasDeletedGroups ? "red" : "#333333"
                    }}>
                    {description}
                  </div>
                }
              </div>
            }
            </div>
          )
        },
        {
          field: "column3",
          title: "",
          type: displayColumn3 ? GroupListColumnType.Icon : GroupListColumnType.Text,
          value: displayColumn3 ? false : ''
        }
      ]
    };

    const group: GroupListGroup = {
      name: application.name,
      expanded: false,
      title: application.name,
      items,
      // hideTitle: true,
      // borderEveryItem: true,
      clickableGroupTitle: true,
      groupValues
    };
    return group;

  }

  // private getLabels(data: StoredObject[]) {
  //   let labels: string = '';

  // }

  private getApplicationsData(applications: IApplication[]) {

    let list = applications.sort((a1, a2) => {
      if (a1.name.toLocaleLowerCase() > a2.name.toLocaleLowerCase()) {
        return 1;
      }
      if (a1.name.toLocaleLowerCase() < a2.name.toLocaleLowerCase()) {
        return -1;
      }
      return 0;
    });

    let groups = list.map(application => {
      return this.getData(application);
    });
    const columnsTitle = [{ title: this.localizationService.strings.TargetingAdmin_ApplicationColumnName, name: "column1" }, { title: this.localizationService.strings.TargetingAdmin_ApplicationColumnProfileDataUserGroups, name: "column2" }, { title: "", name: "column3" }];
    const groupList: GroupList = {
      columns: columnsTitle,
      groups: groups,
      hideFirstColumn: true
    }
    return groupList;
  }

  private getSelectedValues(application: IApplication, list: IStoredObject[], allResults: boolean, defaultValues?: boolean) {
    let value = '';
    if (allResults) {
      return 'All';
    } else {
      if (!defaultValues && list && list.length > 0) {
        let selectedList = list.filter(x => x.value || x.value === undefined);
        const existingGroups = [];
        let hasDeletedGroups = false;
        selectedList.forEach((group: IStoredObject) => {
          if (this.props.targetingStore.groupsList.find((x) => x.id === group.id)) {
            existingGroups.push(group)
          } else {
            hasDeletedGroups = true;
          }
        })
        let maxLabels = existingGroups.length < 4 ? existingGroups.length : 4;
        for (let i = 0; i < maxLabels; i++) {
          value += existingGroups[i].label + ((i === (maxLabels - 1)) ? '' : ', ');
        }
        if (existingGroups.length > 4) {
          value += '...';
        }
        if (hasDeletedGroups && application.default.type === "userGroups") {
          value = this.localizationService.strings.TargetingAdmin_HasDeletedGroups;
        }
      }
    }
    return value;
  }

}